import React, { useState, useEffect, useRef } from 'react';
import { useDrawPageData } from '../hooks/useDrawData';
import { useAuth } from '../contexts/AppKitAuthContext';
import { useScrollLock } from '../hooks/useScrollLock';
import TopNavigation from '../components/TopNavigation';
import AlertPopup, { AlertConfig } from '../components/AlertPopup';
import InsufficientBalancePopup from '../components/InsufficientBalancePopup';
import DXFAcquisitionPopup from '../components/DXFAcquisitionPopup';
import HowItWorksDrawPopup from '../components/HowItWorksDrawPopup';
import DrawVerificationModal from '../components/DrawVerificationModal';
import styles from '../styles/DXFRandomDrawPage.module.css';
import { appKitBlockchainService } from '../services/appkitBlockchain';

const DXFRandomDrawPage: React.FC = () => {
  const [showParticipationSuccess, setShowParticipationSuccess] = useState(false);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [copiedSeedHash, setCopiedSeedHash] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [alertConfig, setAlertConfig] = useState<AlertConfig | null>(null);
  const [showInsufficientBNBPopup, setShowInsufficientBNBPopup] = useState(false);
  const [showDXFAcquisitionPopup, setShowDXFAcquisitionPopup] = useState(false);
  const [showHowItWorksPopup, setShowHowItWorksPopup] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [selectedDrawForVerification, setSelectedDrawForVerification] = useState<any>(null);
  
  useScrollLock(showVerificationModal);

  const { login, isLoading, isAuthenticated } = useAuth();
  
  const {
    currentDraw,
    eligibility,
    participation,
    history,
    refreshAll
  } = useDrawPageData();

  // Track previous authentication state to detect sign-in
  const prevAuthenticatedRef = useRef(isAuthenticated);

  useEffect(() => {
    // Refresh data when user signs in (auth changes from false to true)
    if (!prevAuthenticatedRef.current && isAuthenticated) {
      console.log('User signed in, refreshing all draw data (currentDraw + eligibility)');
      refreshAll();
    }
    prevAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated, refreshAll]);

  useEffect(() => {
    // Check eligibility if authenticated, but only if:
    // 1. Not currently loading
    // 2. No eligibility data yet
    // 3. No recent network errors
    if (isAuthenticated && 
        !eligibility.loading && 
        !eligibility.eligibility && 
        !eligibility.hasRecentError) {
      eligibility.checkEligibility();
    }
  }, [isAuthenticated, eligibility]);

  const handleParticipate = async () => {
    try {
      await participation.participate();
      setShowParticipationSuccess(true);
      
      // Refresh all data after successful participation
      await refreshAll();
      
      // Hide success message after 5 seconds
      setTimeout(() => setShowParticipationSuccess(false), 5000);
    } catch (error) {
      console.error('Participation failed:', error);
      
      // Refresh all data on error to get latest state
      refreshAll();
      
      // Show error in AlertPopup
      setAlertConfig({
        type: 'error',
        title: 'Participation Failed',
        message: error instanceof Error ? error.message : 'An unknown error occurred',
        confirmText: 'OK',
        duration: 0
      });
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    history.refetch(newPage, 90);
  };

  const handleCopySeedHash = (seedHash: string) => {
    navigator.clipboard.writeText(seedHash).then(() => {
      setCopiedSeedHash(true);
      setTimeout(() => setCopiedSeedHash(false), 1000);
      console.log('Seed hash copied to clipboard');
    }).catch(err => {
      console.error('Failed to copy seed hash:', err);
    });
  };

  const handleStartGame = () => {
    window.location.href = '/';
  };

  const handleGetBNB = () => {
    setShowInsufficientBNBPopup(true);
  };

  const handleGetDXF = () => {
    setShowDXFAcquisitionPopup(true);
  };

  const handleCloseBNBPopup = async () => {
    setShowInsufficientBNBPopup(false);
    // Refresh eligibility after closing popup (user may have acquired BNB)
    console.log('Refreshing balance after closing BNB popup');
    await eligibility.checkEligibility();
  };

  const handleCloseDXFPopup = async () => {
    setShowDXFAcquisitionPopup(false);
    // Refresh eligibility after closing popup (user may have acquired DXF)
    console.log('Refreshing balance after closing DXF popup');
    await eligibility.checkEligibility();
  };

  const handleVerifyDraw = (draw: any) => {
    setSelectedDrawForVerification(draw);
    setShowVerificationModal(true);
  };

  const handleCloseVerificationModal = () => {
    setShowVerificationModal(false);
    setSelectedDrawForVerification(null);
  };

  const handleSignIn = async () => {
    const clickId = Math.random().toString(36).substr(2, 9);
    console.log(`🖱️ [DRAW-CLICK-${clickId}] Sign In button clicked on draw page`);
    
    try {
      console.log(`🖱️ [DRAW-CLICK-${clickId}] About to call login()`);
      await login();
      console.log(`🖱️ [DRAW-CLICK-${clickId}] Login completed successfully`);
    } catch (error) {
      console.error(`🖱️ [DRAW-CLICK-${clickId}] Sign in failed:`, error);
      // Error handling is done in the auth context
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateAddress = (address: string) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };


  return (
    <div className={styles.drawPage}>
      <TopNavigation fixed />
      <div className={styles.drawContainer}>
        {/* Header with Current Draw */}
        <div className={styles.drawHeader}>
          <div className={styles.headerContent}>
            <h1>🎲 DXF Daily Draw</h1>
            <button 
              className={styles.howItWorksLink}
              onClick={() => setShowHowItWorksPopup(true)}
            >
              How it works?
            </button>
            <p>Daily draws with DXF token rewards! Participate by holding BNB+DXF or starting a game.</p>
          </div>
          
          {currentDraw.loading ? (
            <div className={styles.loadingSpinner}>Loading current draw...</div>
          ) : currentDraw.error ? (
            <div className={styles.errorMessage}>Error: {currentDraw.error}</div>
          ) : currentDraw.currentDraw ? (
            <div className={styles.currentDrawInfo}>
              <div className={styles.drawInfo}>
                <div className={styles.drawDate}>
                  <span className={styles.label}>Date (UTC):</span>
                  <span className={styles.value}>{formatDate(currentDraw.currentDraw.date)}</span>
                </div>
                <div className={styles.drawParticipants}>
                  <span className={styles.label}>Participants:</span>
                  <span className={styles.value}>{currentDraw.currentDraw.participantCount}</span>
                </div>
                <div 
                  className={styles.drawSeedhash}
                  onClick={() => setIsTooltipVisible(!isTooltipVisible)}
                  onMouseEnter={() => setIsTooltipVisible(true)}
                  onMouseLeave={() => setIsTooltipVisible(false)}
                >
                  <span className={styles.label}>Seed Hash:</span>
                  <div className={styles.seedhashContainer}>
                    <span className={`${styles.value} ${styles.seedhashValue}`}>
                      {currentDraw.currentDraw.seedHash ? (
                        <>
                          <span className={styles.seedhashDesktop}>
                            {`${currentDraw.currentDraw.seedHash.slice(0, 8)}...${currentDraw.currentDraw.seedHash.slice(-6)}`}
                          </span>
                          <span className={styles.seedhashMobile}>
                            {`${currentDraw.currentDraw.seedHash.slice(0, 4)}...${currentDraw.currentDraw.seedHash.slice(-4)}`}
                          </span>
                        </>
                      ) : 
                        'Loading...'
                      }
                    </span>
                    {currentDraw.currentDraw.seedHash && (
                      <button 
                        className={styles.copyButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          currentDraw.currentDraw && handleCopySeedHash(currentDraw.currentDraw.seedHash);
                        }}
                        title="Copy full seed hash"
                      >
                        {copiedSeedHash ? 'DONE' : '📋'}
                      </button>
                    )}
                  </div>
                  
                  {/* Tooltip */}
                  <div className={`${styles.seedhashTooltip} ${isTooltipVisible ? styles.visible : ''}`}>
                    <h4>DRAW SEED HASH</h4>
                    <p>PRE-COMMITTED BEFORE DRAW STARTS:</p>
                    <ul>
                      <li>WINNER SELECTION PRE-DETERMINED</li>
                      <li>CANNOT BE CHANGED DURING DRAW</li>
                      <li>ENSURES COMPLETE FAIRNESS</li>
                      <li>COPY HASH TO VERIFY ANYTIME</li>
                    </ul>
                    <p>VERIFY DRAW FAIRNESS INDEPENDENTLY</p>
                  </div>
                </div>
              </div>

            </div>
          ) : null}
        </div>

        {/* Eligibility & Participation Section */}
        <div className={styles.participationSection}>
          <h2>Participation</h2>
          
          {!isAuthenticated ? (
            // Not signed in state
            <div className={styles.participationNotSignedIn}>
              <h3>🔒 Sign In Required</h3>
              <p>Connect your wallet to participate in the daily DXF draw.</p>
              <button 
                className={`${styles.btn} ${styles.btnPrimary}`} 
                onClick={handleSignIn}
                disabled={isLoading}
              >
                {isLoading ? 'Connecting...' : 'Sign In'}
              </button>
            </div>
          ) : eligibility.loading ? (
            <div className={styles.loadingSpinner}>Checking eligibility...</div>
          ) : eligibility.error ? (
            <div className={styles.errorMessage}>
              {eligibility.hasRecentError ? (
                <>
                  <div>Network connection issue detected</div>
                  <div style={{fontSize: '0.75rem', opacity: 0.8, marginTop: '5px'}}>
                    Retrying automatically in a few seconds...
                  </div>
                </>
              ) : (
                <>Error: {eligibility.error}</>
              )}
            </div>
          ) : eligibility.eligibility ? (
            eligibility.eligibility.alreadyParticipated && currentDraw.currentDraw?.userParticipation ? (
              // Already joined state
              <div className={styles.participationJoined}>
                <h3>✅ Joined at {formatTime(currentDraw.currentDraw.userParticipation.participatedAt)}</h3>
                <p>You have joined today's draw, your seq number is <span className={styles.seqInfo}>#{currentDraw.currentDraw.userParticipation.seqNumber}</span></p>
                <p>Come back to check the result tomorrow!</p>
                {/* <button className={`${styles.btn} ${styles.btnJoined}`} disabled>
                  Joined
                </button> */}
              </div>
            ) : (
              // Eligible to join state
              <div>
                <div className={styles.eligibilityParts}>
                  <div className={styles.eligibilityPart}>
                    <h4>
                      Start a Game Today 
                      {eligibility.eligibility.hasPlayedToday ? ' [✓]' : ''}
                    </h4>
                    <p>Play at least one game today to qualify for the draw</p>
                    <button 
                      className={`${styles.conditionButton} ${eligibility.eligibility.hasPlayedToday ? styles.completed : styles.incomplete}`}
                      onClick={handleStartGame}
                      disabled={eligibility.eligibility.hasPlayedToday}
                    >
                      {eligibility.eligibility.hasPlayedToday ? '[✓] START A GAME' : '[ ] START A GAME'}
                    </button>
                  </div>
                  
                  <div className={styles.eligibilityOr}>OR</div>
                  
                  <div className={styles.eligibilityPart}>
                    <h4>
                      Hold BNB and DXF
                      {(eligibility.eligibility.hasSufficientBNB && eligibility.eligibility.hasSufficientDXF) ? ' [✓]' : ''}
                    </h4>
                    <p>Hold at least {eligibility.eligibility.thresholds.minBnb} BNB + {eligibility.eligibility.thresholds.minDxf} DXF tokens in your wallet</p>
                    <div className={styles.eligibilityChecks}>
                      <button 
                        className={`${styles.conditionButton} ${eligibility.eligibility.hasSufficientBNB ? styles.completed : styles.incomplete}`}
                        onClick={handleGetBNB}
                        disabled={eligibility.eligibility.hasSufficientBNB}
                      >
                        {eligibility.eligibility.hasSufficientBNB ? '[✓]' : '[ ]'} BNB ≥ {eligibility.eligibility.thresholds.minBnb}
                      </button>
                      <button 
                        className={`${styles.conditionButton} ${eligibility.eligibility.hasSufficientDXF ? styles.completed : styles.incomplete}`}
                        onClick={handleGetDXF}
                        disabled={eligibility.eligibility.hasSufficientDXF}
                      >
                        {eligibility.eligibility.hasSufficientDXF ? '[✓]' : '[ ]'} DXF ≥ {eligibility.eligibility.thresholds.minDxf}
                      </button>
                    </div>
                  </div>
                </div>


                {eligibility.eligibility.isEligible ? (
                  <div style={{textAlign: 'center', marginTop: '20px'}}>
                    <button 
                      className={`${styles.btn} ${styles.btnPrimary}`}
                      onClick={handleParticipate}
                      disabled={participation.loading}
                    >
                      {participation.loading ? 'Joining Draw...' : 'Join Today\'s Draw'}
                    </button>
                  </div>
                ) : !eligibility.eligibility.alreadyParticipated && !currentDraw.currentDraw?.userParticipation ? (
                  <div className={styles.requirementsMessage}>
                    <div className={styles.messageText}>
                      Complete <span className={styles.highlight}>either requirement</span> above to participate in the draw
                    </div>
                  </div>
                ) : null}
              </div>
            )
          ) : null}


          {showParticipationSuccess && (
            <div className={styles.successMessage}>
              🎉 Successfully joined today's draw! Good luck!
            </div>
          )}
        </div>

        {/* Draw History Section */}
        <div className={styles.historySection}>
          <h2>Draw History</h2>
          
          {history.loading ? (
            <div className={styles.loadingSpinner}>Loading history...</div>
          ) : history.error ? (
            <div className={styles.errorMessage}>Error: {history.error}</div>
          ) : history.history ? (
            <div className={styles.historyContent}>
              <div className={styles.historyTable}>
                <div className={styles.tableHeader}>
                  <div className={styles.colDate}>Date (UTC)</div>
                  <div className={styles.colParticipants}>Participants</div>
                  <div className={styles.colWinner}>Winner</div>
                  <div className={styles.colTx}>Transaction</div>
                </div>
                
                {history.history.draws.map((draw, index) => (
                  <div key={draw.date} className={styles.tableRow}>
                    <div className={styles.colDate}>{formatDate(draw.date)}</div>
                    <div className={styles.colParticipants}>{draw.participantCount}</div>
                    <div className={styles.colWinner}>
                      {draw.winnerAddress ? (
                        <span className={styles.winnerAddress} title={draw.winnerAddress}>
                          #{draw.winnerSeqNumber} - {truncateAddress(draw.winnerAddress)}
                          {draw.status === 'completed' && draw.seed && (
                            <button 
                              className={styles.verifyButton}
                              onClick={() => handleVerifyDraw(draw)}
                              title="Verify this draw's fairness"
                            >
                              Verify
                            </button>
                          )}
                        </span>
                      ) : (
                        <span className={styles.noWinner}>-</span>
                      )}
                    </div>
                    <div className={styles.colTx}>
                      {draw.mintTxHash ? (
                        <a 
                          href={appKitBlockchainService.explorerTx(draw.mintTxHash)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.txLink}
                        >
                          {draw.mintTxHash.slice(0, 8)}...{draw.mintTxHash.slice(-6)}
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 16 16"
                            fill="currentColor"
                            className={styles.arrowIcon}
                          >
                            <path d="M3.5 3.5V12.5H12.5V8H14V12.5C14 13.0523 13.5523 13.5 13 13.5H3C2.44772 13.5 2 13.0523 2 12.5V3C2 2.44772 2.44772 2 3 2H7.5V3.5H3.5Z" />
                            <path d="M14 2V7H12.5V4.20711L6.85355 9.85355L5.79289 8.79289L11.4393 3.14645H8.5V2H14Z" />
                          </svg>
                        </a>
                      ) : (
                        <span className={styles.noTx}>-</span>
                      )}
                    </div>
                    
                    {/* Mobile verify button */}
                    {draw.status === 'completed' && draw.seed && (
                      <button 
                        className={styles.mobileVerifyButton}
                        onClick={() => handleVerifyDraw(draw)}
                        title="Verify this draw's fairness"
                      >
                        Verify Draw Fairness
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {history.history.pagination.totalPages > 1 && (
                <div className={styles.pagination}>
                  <button 
                    className={styles.pageButton}
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    ← Previous
                  </button>
                  
                  <span className={styles.pageInfo}>
                    Page {currentPage} of {history.history.pagination.totalPages}
                  </span>
                  
                  <button 
                    className={styles.pageButton}
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === history.history.pagination.totalPages}
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          ) : null}
        </div>

      </div>
      
      {/* Alert Popup */}
      <AlertPopup
        alert={alertConfig}
        onClose={() => setAlertConfig(null)}
      />
      
      {/* Insufficient BNB Popup */}
      <InsufficientBalancePopup
        isOpen={showInsufficientBNBPopup}
        onClose={handleCloseBNBPopup}
        requiredAmount={eligibility.eligibility?.thresholds.minBnb || '0.02'}
        context="draw"
      />
      
      {/* DXF Acquisition Popup */}
      <DXFAcquisitionPopup
        isOpen={showDXFAcquisitionPopup}
        onClose={handleCloseDXFPopup}
        requiredAmount={eligibility.eligibility?.thresholds.minDxf || '100'}
      />
      
      {/* How It Works Popup */}
      <HowItWorksDrawPopup
        isOpen={showHowItWorksPopup}
        onClose={() => setShowHowItWorksPopup(false)}
        thresholds={eligibility.eligibility?.thresholds}
      />
      
      {/* Draw Verification Modal */}
      {selectedDrawForVerification && (
        <DrawVerificationModal
          isOpen={showVerificationModal}
          onClose={handleCloseVerificationModal}
          draw={selectedDrawForVerification}
        />
      )}
    </div>
  );
};

export default DXFRandomDrawPage;